import React from 'react'

import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES, Document } from '@contentful/rich-text-types'

import CodeBlock from 'src/components/blocks/CodeBlock'
import Note from 'src/components/blocks/Note'
import { RichTextQuoteEmbed } from 'src/modules/organisms/richtext-quote-embed'
import {
  BlogCTA,
  BlogTable,
  BlogPodcastWidget
} from 'src/components/blog/BlogPostEmbeds'

// Recursively extract text from rich text nodes
const extractText = (node: any): string => {
  if (!node) return ''
  if (typeof node === 'string') return node
  if (node.value) return node.value
  if (node.content && Array.isArray(node.content)) {
    return node.content.map(extractText).join('')
  }
  return ''
}

const formatTextToId = (text: string) =>
  (text || '')
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .split(/\s+/)
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase()
      } else {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      }
    })
    .join('')

// Check if a rich text node is a code snippet
const isRichTextCodeSnippet = (item: any) =>
  item.marks &&
  item.marks.length === 1 &&
  item.marks[0].type === 'code' &&
  item.value &&
  item.value.length > 0

const website_url = 'https://flatfile.com'

// Check if a rich text node is only whitespace
const isRichTextWhitespace = (item: any) =>
  item.nodeType === 'text' &&
  (!item.value || (item.value && item.value.replace(/\s/g, '').length === 0))

// Helper to get content type from raw Contentful API data
const getContentType = (target: any): string | null => {
  // Raw Contentful API structure
  if (target?.sys?.contentType?.sys?.id) {
    return target.sys.contentType.sys.id
  }
  // Gatsby structure (fallback for mixed usage)
  if (target?.internal?.type) {
    return target.internal.type.replace('Contentful', '').toLowerCase()
  }
  return null
}

// Helper to get field value - handles both raw API (fields.x) and Gatsby (x) structures
const getField = (target: any, fieldName: string) => {
  return target?.fields?.[fieldName] ?? target?.[fieldName]
}

// Rich text options for raw Contentful API data
export const richTextOptionsLocal = {
  renderNode: {
    [INLINES.HYPERLINK]: (node: any) => {
      return (
        <a
          href={node.data.uri}
          target={`${node.data.uri.includes('#') ? '_self' : '_blank'}`}
          rel={`${
            node.data.uri.startsWith(website_url) ? '' : 'noopener noreferrer'
          }`}
        >
          {node.content[0].value}
        </a>
      )
    },
    [BLOCKS.HEADING_2]: (node: any, children: React.ReactNode) => {
      const title = extractText(node)
      const id = formatTextToId(title)
      return (
        <h2 className='title-h3' id={id}>
          <a id={id} href={`#${id}`}>
            {children}
          </a>
        </h2>
      )
    },
    [BLOCKS.HEADING_3]: (node: any, children: React.ReactNode) => {
      const title = extractText(node)
      const id = formatTextToId(title)
      return (
        <h3 className='title-h3' id={id}>
          {children}
        </h3>
      )
    },
    [BLOCKS.HEADING_4]: (node: any, children: React.ReactNode) => {
      const title = extractText(node)
      const id = formatTextToId(title)
      return (
        <h4 className='title-h4' id={id}>
          {children}
        </h4>
      )
    },
    [BLOCKS.HEADING_5]: (node: any, children: React.ReactNode) => {
      const title = extractText(node)
      const id = formatTextToId(title)
      return (
        <h5 className='title-h5' id={id}>
          {children}
        </h5>
      )
    },
    [BLOCKS.HEADING_6]: (node: any, children: React.ReactNode) => {
      const title = extractText(node)
      const id = formatTextToId(title)
      return (
        <h6 className='title-h5' id={id}>
          {children}
        </h6>
      )
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => {
      const content = node.content
      const codeContent = content.filter(isRichTextCodeSnippet)
      const nonCodeContent = content.filter(
        (item: any) =>
          !isRichTextCodeSnippet(item) && !isRichTextWhitespace(item)
      )
      const nonWhitespaceContent = content.filter(
        (item: any) => !isRichTextWhitespace(item)
      )
      if (codeContent.length > 0 && nonCodeContent.length === 0) {
        return (
          <div className='gatsby-highlight'>
            <pre className='language-javascript'>{children}</pre>
          </div>
        )
      } else if (nonWhitespaceContent.length === 0) {
        return <></>
      }
      return <p className='paragraph mb-6 text-[#808080]'>{children}</p>
    },
    // Custom embedded content - handles raw Contentful API structure
    [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
      const target = node.data.target
      const contentType = getContentType(target)

      if (
        contentType === 'figureWithCaption' ||
        contentType === 'figurewithcaption'
      ) {
        const imageUrl =
          getField(target, 'image')?.fields?.file?.url ||
          getField(target, 'image')?.url
        const caption = getField(target, 'caption')
        const captionAlignment =
          getField(target, 'captionAlignment') || 'center'
        return (
          <figure className='!py-8'>
            <img
              className='max-w-full mx-auto'
              src={imageUrl?.startsWith('//') ? `https:${imageUrl}` : imageUrl}
              alt={caption || ''}
            />
            <figcaption
              className={`text-${captionAlignment} text-gray-11 font-ital font-no-synthesis py-1`}
            >
              {caption}
            </figcaption>
          </figure>
        )
      } else if (contentType === 'cta') {
        const ctaText = getField(target, 'ctaText')
        const text = getField(target, 'text')
        const title = getField(target, 'title')
        const href = getField(target, 'href')
        const directSignup = getField(target, 'directSignup')
        return (
          <BlogCTA
            ctaText={ctaText}
            text={text}
            title={title}
            href={href}
            directSignup={directSignup}
          />
        )
      } else if (contentType === 'table') {
        const tableData = getField(target, 'table')?.tableData
        return <BlogTable rows={tableData} />
      } else if (contentType === 'codeBlock' || contentType === 'codeblock') {
        const language = getField(target, 'language') || 'javascript'
        const code = getField(target, 'code')?.code || getField(target, 'code')
        return (
          <CodeBlock className={`language-${language}`} copy>
            {(code || '').replace(/```/g, '')}
          </CodeBlock>
        )
      } else if (
        contentType === 'anchorPoint' ||
        contentType === 'anchorpoint'
      ) {
        const name = getField(target, 'name')
        return <div id={name}></div>
      } else if (contentType === 'video') {
        const url = getField(target, 'url')
        const title = getField(target, 'title')
        return (
          <iframe
            allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'
            allowFullScreen
            frameBorder='0'
            height='315'
            src={url}
            title={title}
            width='560'
          ></iframe>
        )
      } else if (
        contentType === 'wistiaVideo' ||
        contentType === 'wistiavideo'
      ) {
        const video = getField(target, 'video')
        const title = getField(target, 'title')
        const hashedId = video?.items?.[0]?.hashed_id
        if (!hashedId) return <></>
        return (
          <div
            style={{
              aspectRatio: '16/9',
              margin: '1.75rem auto',
              position: 'relative',
              border: '2px solid black'
            }}
          >
            <div
              style={{
                height: '100%',
                left: 0,
                position: 'absolute',
                top: 0,
                width: '100%'
              }}
            >
              <iframe
                src={`https://fast.wistia.net/embed/iframe/${hashedId}?seo=true&videoFoam=true`}
                title={title}
                allow='autoplay; fullscreen'
                allowTransparency={true}
                frameBorder={0}
                scrolling='no'
                className='wistia_embed'
                name='wistia_embed'
                width='100%'
                height='100%'
              />
            </div>
          </div>
        )
      } else if (contentType === 'iframe') {
        return (
          <iframe
            allow={getField(target, 'allow')}
            allowFullScreen={getField(target, 'allowfullscreen')}
            height={getField(target, 'height')}
            loading={getField(target, 'loading')}
            name={getField(target, 'name')}
            referrerPolicy={getField(target, 'referrerpolicy')}
            src={getField(target, 'src')}
            title={getField(target, 'title')}
            width={getField(target, 'width')}
          ></iframe>
        )
      } else if (
        contentType === 'podcastEpisode' ||
        contentType === 'podcastepisode'
      ) {
        return (
          <BlogPodcastWidget
            applePodcastLink={getField(target, 'applePodcastLink')}
            googlePodcastLink={getField(target, 'googlePodcastLink')}
            sounderLink={getField(target, 'sounderLink')}
            spotifyLink={getField(target, 'spotifyLink')}
            stitcherLink={getField(target, 'stitcherLink')}
          />
        )
      } else if (contentType === 'infoBox' || contentType === 'infobox') {
        const text = getField(target, 'text')?.text || getField(target, 'text')
        return <Note message={text} />
      } else if (contentType === 'quote') {
        const body = getField(target, 'body')?.body || getField(target, 'body')
        const quotee = getField(target, 'quotee')
        const quoteeData = quotee?.fields
          ? {
              fullName: quotee.fields.fullName,
              jobTitle: quotee.fields.jobTitle
            }
          : quotee
        return <RichTextQuoteEmbed body={body} quotee={quoteeData} />
      }
      // Unknown content type
      return <></>
    },
    // Images - handles raw Contentful API structure
    [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
      const target = node.data.target
      // Raw API structure: target.fields.file.url
      // Gatsby structure: target.file.url
      const fileUrl = target?.fields?.file?.url || target?.file?.url
      const description =
        target?.fields?.description || target?.description || ''

      if (!fileUrl) return <></>

      const fullUrl = fileUrl.startsWith('//') ? `https:${fileUrl}` : fileUrl
      return <img alt={description} src={fullUrl} />
    }
  }
}

// Helper function to render rich text from raw Contentful API data
export const renderLocalRichText = (document: Document | any) => {
  if (!document) return null
  return documentToReactComponents(document, richTextOptionsLocal)
}
